/**
 * One rule for display-sized product images, shared by the Worker (server-rendered HTML), the store
 * and till web pages, and mirrored in shop-app/src/lib/images.ts (the verify suite checks they agree).
 *
 * Relay's own copies (`/media/...` on app.nundi.co.za, www.zsmerch.co.za, zsmerch.co.za, or a relative
 * path) are served as PLAIN FILES at `/media/_w/<width>/<key>`: the three sizes are made once on the
 * owner's PC (fetcher/make-image-sizes.mjs) and stored in R2 as `sized/<width>/<key>.webp`. This
 * replaced Cloudflare Image Transformations, which bill every unique photo x size per month (~$10 for
 * 8.5k photos x 3). The route (media-sized.ts) falls back to the unresized original until a sized copy
 * exists, so a not-yet-processed photo still shows. The URL keeps the media URL's OWN origin (a
 * relative path stays on the page's host): it is a plain Worker route answering on every host, so no
 * rewrite to the nundi zone is needed any more. Shopify CDN URLs keep a `width=` query. Anything else
 * (data:, foreign host) is unchanged.
 */
/** Origin for a relative path when the caller gives no page `base`. */
export const TRANSFORM_ORIGIN = "https://app.nundi.co.za";
export const IMAGE_WIDTHS = [300, 600, 900] as const;
/** Kept for callers; sized copies are always WebP now, so the value no longer changes the URL. */
export type ImageFormat = "auto" | "webp";
/** URL prefix of the sized-copy route, `/media/_w/<width>/<key>`. */
export const SIZED_PREFIX = "/media/_w/";

const RELAY_HOST = /(^|\.)(nundi\.co\.za|zsmerch\.co\.za)$/i;

/** Parsed Relay media URL, or null when it is not one of ours. `base` resolves relative paths. */
export function relayMedia(url: string, base = TRANSFORM_ORIGIN): URL | null {
  try {
    const u = new URL(url, base);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    return RELAY_HOST.test(u.hostname) && u.pathname.startsWith("/media/") ? u : null;
  } catch {
    return null;
  }
}

const isShopifyCdn = (url: string) => /^https?:\/\/[^/]*shopify[^/]*\//i.test(url) || /\/cdn\/shop\//.test(url);

/** The ladder width that covers `width` (smallest rung >= it, else the largest): only these exist on disk. */
export const snapWidth = (width: number): number => IMAGE_WIDTHS.find((w) => w >= width) ?? 900;

/** A `width`-wide copy where the host can make one; otherwise the URL unchanged. */
export function sizedImageUrl(url: string, width: number, opts: { format?: ImageFormat; base?: string } = {}): string {
  const own = relayMedia(url, opts.base);
  if (own) return `${own.origin}${SIZED_PREFIX}${snapWidth(width)}/${own.pathname.slice("/media/".length)}${own.search}`;
  if (!isShopifyCdn(url)) return url;
  try {
    const u = new URL(url, opts.base ?? TRANSFORM_ORIGIN);
    u.searchParams.set("width", String(Math.round(width)));
    return u.toString();
  } catch {
    return url;
  }
}

/** True when `sizedImageUrl` can resize this URL. */
export const canSize = (url: string, base?: string): boolean => relayMedia(url, base) !== null || isShopifyCdn(url);

/** `srcset` text for the 300/600/900 ladder, or "" when the URL cannot be resized. */
export function imageSrcset(url: string, opts: { base?: string } = {}): string {
  return canSize(url, opts.base) ? IMAGE_WIDTHS.map((w) => `${sizedImageUrl(url, w, opts)} ${w}w`).join(", ") : "";
}
